import { useContext, useState } from "react";

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Skeleton,
} from "@upstageai/ui";
import { useTranslation } from "react-i18next";

import ApiCodeContent from "./ApiCodeContent";
import { Api } from "../../components/Icon/generated/Api";
import { EndpointType, getDocumentationUrl } from "../../constants/Endpoints";
import { DEMO_MODE_FEATURE } from "../../constants/FeatureFlag";
import { FEATURE } from "../../constants/FeatureFlag";
import { useDevice } from "../../hooks/useDevice";
import { ValidatorFileContext } from "../../hooks/useFile";
import { endpointStore } from "../../utils/mobx/EndpointStore";
import { cn } from "../../utils/style";

const GetApiDialogButtonSkeleton = () => {
  return <Skeleton className={cn("size-9 rounded-lg")} />;
};

export default function GetApiDialog() {
  const { t } = useTranslation();
  const { endpointType } = endpointStore;
  const { isMobile } = useDevice();
  const { inferenceState } = useContext(ValidatorFileContext);

  const hasGetAPIButton = FEATURE === DEMO_MODE_FEATURE;

  const [isOpen, setIsOpen] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!hasGetAPIButton) return null;

  if (inferenceState.isInfering) return <GetApiDialogButtonSkeleton />;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button size={isMobile ? "icon" : "default"} className="gap-1.5">
          {isMobile && <Api />}
          {!isMobile && "Get API"}
        </Button>
      </DialogTrigger>
      <DialogContent
        className={cn("flex h-fit max-h-[90%] max-w-[600px] flex-col gap-4")}
      >
        <DialogHeader className="flex flex-row flex-wrap items-center justify-between gap-1 space-y-0">
          <DialogTitle>Get api from this document</DialogTitle>
          <div className="flex flex-1 justify-end">
            <Button
              variant="outline"
              size="xs"
              className="gap-1 self-end"
              onClick={() =>
                window.open(
                  getDocumentationUrl(endpointType as EndpointType),
                  "_blank",
                )
              }
            >
              Go to documentation
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* 모달 컨텐츠 */}
        <div className="flex-1">
          <div className="flex w-full flex-col gap-6">
            <div className="w-full">
              <ApiCodeContent endpointType={endpointType} />
            </div>
          </div>
        </div>

        <DialogFooter className="flex h-fit items-end">
          <div className="flex w-full flex-wrap items-center justify-between gap-1">
            <div className="body">
              <span>Don&apos;t have an API key? Go to </span>
              <a
                href="https://console.upstage.ai/api-keys"
                className="text-accent-blue underline underline-offset-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                API keys
              </a>
              <span> to generate one.</span>
            </div>
            <div className="flex flex-1 justify-end">
              <Button variant="ghost" onClick={handleClose}>
                {t(`actions.close`)}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
